import { useCallback, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { MyOrderStatus } from 'entities/common/my-ticket/components/my-order-status'
import { MyOrderStatusPayload } from 'entities/common/my-ticket/model/types'
import type { KyrgyzstanBookingStatus } from 'entities/kyrgyzstan'
import { useBookingStatusDialog } from 'entities/kyrgyzstan/ui/booking-card/lib/use-booking-status-dialog.ts'
import {
  BookingStatusDialogContent,
  BookingStatusDialogItemView,
} from 'entities/kyrgyzstan/ui/booking-card/ui/status-dialog'

import type { PaymentStatus } from 'shared/api/kyrgyzstan/orders'

interface Props {
  statusInfo?: MyOrderStatusPayload
  title?: string
  color?: string
  background?: string
}

export const MyOrderInfoKyrgyzstan = ({
  statusInfo,
  title,
  color,
  background,
}: Props) => {
  const { t } = useTranslation()

  const bookingDialog = useBookingStatusDialog({
    status: statusInfo?.status as KyrgyzstanBookingStatus,
    statusInfo: {
      arriveDate: statusInfo?.start_date,
      outDate: statusInfo?.end_date,
    },
    paymentStatus: statusInfo?.payment_status as PaymentStatus,
  })

  const dialogViewItems = useMemo<BookingStatusDialogItemView[]>(
    () =>
      bookingDialog.dialogItems.map((item) => ({
        icon: item.icon,
        text: item.withDates
          ? t(item.translationKey, bookingDialog.dateOptions)
          : t(item.translationKey),
      })),
    [bookingDialog.dialogItems, bookingDialog.dateOptions, t],
  )

  const statusTitle = useMemo(
    () => t(bookingDialog.statusTranslationKey),
    [bookingDialog.statusTranslationKey, t],
  )

  const handleStatusClick = useCallback(() => {
    if (dialogViewItems.length === 0) return
    bookingDialog.openStatusDialog(
      <BookingStatusDialogContent
        title={statusTitle}
        theme={bookingDialog.statusTheme}
        items={dialogViewItems}
      />,
    )
  }, [bookingDialog, dialogViewItems, statusTitle])

  return (
    <MyOrderStatus
      title={title}
      color={color}
      background={background}
      showInfoIcon
      onClick={handleStatusClick}
    />
  )
}
